import { useTranslate } from "@opensea/next-translate"
import { CenterAligned } from "@opensea/ui-kit"
import { ErrorBoundary } from "@sentry/nextjs"
import { range, keyBy } from "lodash"
import { useRouter } from "next/router"
import React, { Suspense, useCallback, useEffect, useMemo } from "react"
import { graphql } from "react-relay"
import { Button } from "@/design-system/Button"
import { Text } from "@/design-system/Text"
import { useUpdateUserTransactionState } from "@/features/rpc/useUpdateTransactionStatusPending"
import { useChains } from "@/hooks/useChains"
import type { ChainIdentifier } from "@/hooks/useChains/types"
import type { TransactionListQuery } from "@/lib/graphql/__generated__/TransactionListQuery.graphql"
import { useRefetchableLazyLoadQuery } from "@/lib/graphql/useRefetchableLazyLoadQuery"
import { useIsWalletVisible } from "@/providers/Wallet/selectors"
import { TxItem } from "./components/TxItem.react"
import { Transaction } from "./Transaction.react"
import { useTransactionListPollingInterval } from "./useTransactionListPollingInterval"

const MAX_TRANSACTION_HISTORY_COUNT = 20

const TRANSACTION_LIST_QUERY = graphql`
  query TransactionListQuery(
    $address: AddressScalar!
    $chain: ChainScalar!
    $count: Int!
  ) {
    transactionHistory(fromAddress: $address, chain: $chain, first: $count) {
      nonce
      fromAddress
      receipt(chain: $chain) {
        transactionHash
        status
        ...Transaction_receipt
      }
      metadata(chain: $chain) {
        type
        ...Transaction_metadata
      }
    }
    userTransactions(
      fromAddress: $address
      chain: $chain
      states: [PENDING, CANCELLED]
    ) {
      relayId
      transactionHash
      nonce
      state
      createdDate
      transactionMetadata {
        type
        ...Transaction_metadata
      }
      ...Transaction_userTransaction
    }
  }
`

type TransactionListProps = {
  address: string
  chain: ChainIdentifier
}

function LazyTransactionList({ address, chain }: TransactionListProps) {
  const t = useTranslate("transactions")
  const { getBlockExplorerAddressUrl } = useChains()

  const [{ transactionHistory, userTransactions }, refetch] =
    useRefetchableLazyLoadQuery<TransactionListQuery>(TRANSACTION_LIST_QUERY, {
      address,
      chain,
      count: MAX_TRANSACTION_HISTORY_COUNT + 1, // Request 1 extra to determine if there are more transactions
    })
  const pendingTransactions = userTransactions.filter(
    tx => tx.state === "PENDING",
  )
  const canceledTransactions = userTransactions.filter(
    tx => tx.state === "CANCELLED",
  )

  const router = useRouter()
  const refresh = Boolean(router.query.refresh)
  useEffect(() => {
    if (refresh) {
      refetch()
      // Reset query param
      void router.push(router.pathname, undefined, { shallow: true })
    }
  }, [refetch, refresh, router])

  const pollingInterval = useTransactionListPollingInterval()
  const isWalletVisible = useIsWalletVisible()
  useEffect(() => {
    let pollingIntervalId: NodeJS.Timeout | undefined

    if (pollingInterval && isWalletVisible) {
      pollingIntervalId = setInterval(refetch, pollingInterval)
    }

    return () => {
      clearInterval(pollingIntervalId)
    }
  }, [isWalletVisible, pollingInterval, refetch])

  // Filter out transactions that are in both pending and completed lists
  // After confirmation txns may appear in both lists for a short time
  // since data sources are different (alchemy for completed, PG for pending)
  const completedTransactionsByHash = useMemo(() => {
    return keyBy(transactionHistory, "receipt.transactionHash")
  }, [transactionHistory])
  type TransactionHistoryType = (typeof transactionHistory)[0]
  const getCompletedTxByHash = useCallback(
    (hash: string): TransactionHistoryType | undefined =>
      completedTransactionsByHash[hash],
    [completedTransactionsByHash],
  )
  const filteredPendingTransactions = pendingTransactions.filter(
    tx =>
      tx.transactionHash &&
      !getCompletedTxByHash(tx.transactionHash) &&
      tx.state === "PENDING",
  )

  // TODO: Remove this once BE supports updating user transaction state
  const latestTxNonce = Math.max(
    ...transactionHistory
      .filter(tx => tx.fromAddress === address)
      .map(tx => tx.nonce),
  )
  const updateUserTransactionState = useUpdateUserTransactionState()
  useEffect(() => {
    userTransactions.forEach(tx => {
      if (!tx.transactionHash || tx.state !== "PENDING") {
        return
      }

      const completedTx = getCompletedTxByHash(tx.transactionHash)

      if (completedTx || tx.nonce <= latestTxNonce) {
        void updateUserTransactionState({
          state: completedTx?.receipt?.status ? "SUCCESSFUL" : "CANCELLED",
          userTransactionId: tx.relayId,
        })
      }
    })
  }, [
    completedTransactionsByHash,
    latestTxNonce,
    userTransactions,
    updateUserTransactionState,
    getCompletedTxByHash,
  ])

  const hasMoreTransactions =
    transactionHistory.length === MAX_TRANSACTION_HISTORY_COUNT + 1

  if (!transactionHistory.length) {
    return (
      <CenterAligned className="h-full">
        <Text className="text-center" color="secondary">
          {t("transactions.noTransactionList", "No transactions yet")}
        </Text>
      </CenterAligned>
    )
  }

  return (
    <>
      <TxItem.List>
        {filteredPendingTransactions.map(transaction => (
          <ErrorBoundary
            beforeCapture={scope => {
              scope.setContext("transaction", {
                hash: transaction.transactionHash,
                type: transaction.transactionMetadata?.type,
                relayId: transaction.relayId,
                state: "PENDING",
              })
            }}
            key={transaction.relayId}
          >
            <Transaction
              metadata={transaction.transactionMetadata}
              receipt={null}
              userTransaction={transaction}
            />
          </ErrorBoundary>
        ))}
        {transactionHistory
          .slice(0, MAX_TRANSACTION_HISTORY_COUNT)
          .map(transaction => {
            // merge canceled txn metadata into transactionHistory for display
            const canceledTx = canceledTransactions.find(
              tx =>
                tx.nonce === transaction.nonce &&
                address === transaction.fromAddress,
            )
            return (
              <ErrorBoundary
                beforeCapture={scope => {
                  scope.setContext("transaction", {
                    hash: transaction.receipt?.transactionHash,
                    type: transaction.metadata?.type,
                    state: transaction.receipt?.status
                      ? "SUCCESSFUL"
                      : "FAILED",
                  })
                }}
                key={transaction.receipt?.transactionHash}
              >
                <Transaction
                  isCancelled={Boolean(canceledTx)}
                  metadata={
                    canceledTx?.transactionMetadata ?? transaction.metadata
                  }
                  receipt={transaction.receipt}
                  userTransaction={null}
                />
              </ErrorBoundary>
            )
          })}
      </TxItem.List>
      {hasMoreTransactions ? (
        <Button
          className="m-4"
          href={getBlockExplorerAddressUrl(chain, address)}
          icon="open_in_new"
          iconSide="right"
          target="_blank"
          variant="secondary"
        >
          {t("transactions.viewOnBlockExplorer", "See all")}
        </Button>
      ) : null}
    </>
  )
}

export function TransactionList(props: TransactionListProps) {
  return (
    <Suspense
      fallback={
        <TxItem.List>
          {range(10).map(index => (
            <TxItem.Skeleton key={index} />
          ))}
        </TxItem.List>
      }
    >
      <LazyTransactionList {...props} />
    </Suspense>
  )
}
